import { $api } from "../../shared/api";

export const useRecordAudioConnection = () => {
  let receivedChunks: any = [];
  let audioBuffer: any;
  let playing = false;
  let audioHash = "";
  // how much we already paste in buffer
  let currBufferSize = 0;
  let bufferSize = 1024 * 99;
  let audioFormat = "mp3"; // Default format is mp3
  let expectedAudioSize = 0;
  let receivedAudioSize = 0;

  async function sendMicToWebsocket(deviceId: number) {
    playing = false;
    receivedChunks = [];
    receivedAudioSize = 0;
    try {
      const serverAddress = `${process.env.REACT_APP_WS_VKT_MICROPHONE_URL}${deviceId}`;
      const constraints: any = {
        audio: {
          mandatory: {
            echoCancellation: true,
            sampleRate: 16000, // Set the sample rate to 16 kHz
            channelCount: 1, // Set the channel count to mono
            sampleSize: 16, // Set the sample size to 16-bit
          },
        },
      };
      const stream = await navigator.mediaDevices.getUserMedia(constraints);
      console.log("[VKT] Got media stream:", stream.getAudioTracks()[0].label);
      const audioContext = new AudioContext({
        sampleRate: 16000,
      });
      console.log("[VKT] Created audio context with sample rate:", audioContext.sampleRate);
      const audioInput = audioContext.createMediaStreamSource(stream);

      const scriptProcessor = audioContext.createScriptProcessor(1024, 1, 1);
      audioInput.connect(scriptProcessor);

      // get websocket ticket
      let ticket: string;
      try {
        const res = await $api.get("/ws_ticket");
        ticket = res.data.ticket
        console.log("[VKT] Got WebSocket ticket");
      } catch (e) {
        // 401 is handled by the $api interceptor (logout); just release the mic
        console.error("[VKT] Failed to get WebSocket ticket:", e);
        stream.getTracks().forEach((track) => track.stop());
        return;
      }

      const socket = new WebSocket(`${serverAddress}?ticket=${ticket}`);

      socket.onopen = () => {
        console.log("[VKT] WebSocket connection opened successfully");
      };

      socket.onerror = (error) => {
        console.error("[VKT] WebSocket error:", error);
      };

      socket.onclose = () => {
        console.log("[VKT] WebSocket connection closed");
      };

      socket.onmessage = async (event) => {
        // The 'event.data' contains the received data
        let data = event.data;

        try {
          // if it's json object
          const jsonObject = JSON.parse(data);

          console.log("[VKT] Received JSON data:", jsonObject);
          if (jsonObject.audioLength) {
            bufferSize = parseInt(jsonObject.audioLength, 10);
            expectedAudioSize = bufferSize;
            console.log("[VKT] Updated buffer size to:", bufferSize, "expected audio size:", expectedAudioSize);
            if (jsonObject.hash) {
              audioHash = jsonObject.hash;
              console.log("[VKT] Updated audio hash to:", audioHash);
              // Reset for new audio
              receivedChunks = [];
              receivedAudioSize = 0;
              playing = false;
            }
          }
          if (jsonObject && jsonObject.command && jsonObject.payload) {
            return;
          }
        } catch (err) {
          // If we received binary data
          if (audioFormat === "mp3") {
            // For MP3 format, we'll collect the raw data and decode it later
            const arrayBuffer = await data.arrayBuffer();
            receivedChunks.push(arrayBuffer);
            receivedAudioSize += arrayBuffer.byteLength;
            
            console.log("[VKT] Received MP3 binary data of length:", arrayBuffer.byteLength, 
                      "Total size:", receivedAudioSize, "of", expectedAudioSize, 
                      "Progress:", Math.round((receivedAudioSize / expectedAudioSize) * 100) + "%");
            
            // Only play when we've received all or most of the audio data
            if (receivedAudioSize >= expectedAudioSize * 0.99 && !playing) {
              console.log("[VKT] Starting MP3 playback with full audio data:", 
                         receivedAudioSize, "bytes of expected", expectedAudioSize, "bytes");
              playMp3Audio();
              playing = true;
            }
          } else {
            // Handle WAV format as before
            console.log("[VKT] Received WAV binary data");
            const arrayBuffer = await data.arrayBuffer();
            const receivedInt16Array = new Int16Array(arrayBuffer);
            const receivedFloat32Array = new Float32Array(
              receivedInt16Array.length
            );

            for (let i = 0; i < receivedInt16Array.length; i++) {
              receivedFloat32Array[i] = receivedInt16Array[i] / 0x7fff;
            }

            // Add the received Float32Array to the accumulated chunks
            receivedChunks.push(receivedFloat32Array);
            receivedAudioSize += receivedFloat32Array.length * 4; // 4 bytes per float32
            
            console.log("[VKT] Total WAV data received:", receivedAudioSize, "of", expectedAudioSize);

            // on the first go, we don't have audio buffer
            if (audioBuffer) {
              console.log("[VKT] Copying to existing audio buffer at position:", currBufferSize);
              audioBuffer.copyToChannel(receivedFloat32Array, 0, currBufferSize);
            }

            // Only play when we've received all or most of the audio data
            if (receivedAudioSize >= expectedAudioSize * 0.99 && !playing) {
              console.log("[VKT] Starting WAV playback with complete data");
              playCombinedAudio();
              playing = true;
            }
            currBufferSize += receivedFloat32Array.length;
          }
        }
      };

      scriptProcessor.onaudioprocess = (event) => {
        const buffer = event.inputBuffer.getChannelData(0);

        let bufferSize = buffer.length;

        const int16Array = new Int16Array(bufferSize);
        for (let i = 0; i < bufferSize; i++) {
          // Assuming audioBuffer values are in the range [-1, 1]
          int16Array[i] = buffer[i] * 0x7fff;
        }

        try {
          socket.send(int16Array.buffer);
        } catch (err) {
          console.error("[VKT] Error sending audio data:", err);
        }
      };
      scriptProcessor.connect(audioContext.destination);
    } catch (error) {
      console.error("[VKT] Error in microphone setup:", error);
    }
  }

  const playCombinedAudio = function () {
    try {
      console.log("[VKT] playCombinedAudio called with", receivedChunks.length, "chunks");
      const recieveAudioSampleRate = 16000;
      const audioContext2 = new AudioContext({
        sampleRate: recieveAudioSampleRate,
      });
      console.log("[VKT] Created audio context for playback with sample rate:", audioContext2.sampleRate);
      
      // Concatenate all received chunks into a single Float32Array
      const concatenatedFloat32Array = new Float32Array(
        receivedChunks.reduce((acc: any, chunk: any) => [...acc, ...chunk], [])
      );
      console.log("[VKT] Concatenated Float32Array length:", concatenatedFloat32Array.length);

      // Create an AudioBuffer from the Float32Array with the correct sample rate
      audioBuffer = audioContext2.createBuffer(1, bufferSize, recieveAudioSampleRate);
      audioBuffer.copyToChannel(concatenatedFloat32Array, 0);
      console.log("[VKT] Created audio buffer with duration:", audioBuffer.duration, "seconds");

      // Create a BufferSourceNode
      const source = audioContext2.createBufferSource();
      source.buffer = audioBuffer;

      // Connect the source to the destination (speakers)
      source.connect(audioContext2.destination);
      
      // Add event listeners to track playback state
      source.onended = () => {
        console.log("[VKT] WAV audio playback ended");
      };

      // Start playing the audio
      source.start();
      console.log("[VKT] WAV audio playback started");
    } catch (error) {
      console.error("[VKT] Error playing WAV audio:", error);
    }
  };

  const playMp3Audio = async function () {
    try {
      console.log("[VKT] playMp3Audio called with", receivedChunks.length, "chunks");
      
      // Combine all the array buffers
      const totalLength = receivedChunks.reduce((total: number, chunk: ArrayBuffer) => total + chunk.byteLength, 0);
      console.log("[VKT] Total MP3 data length:", totalLength, "bytes");
      
      const combinedBuffer = new ArrayBuffer(totalLength);
      const uint8View = new Uint8Array(combinedBuffer);
      
      let offset = 0;
      for (const chunk of receivedChunks) {
        uint8View.set(new Uint8Array(chunk), offset);
        offset += chunk.byteLength;
      }
      console.log("[VKT] Combined MP3 buffer created with", offset, "bytes");

      // Create a blob from the combined buffer with MP3 MIME type
      const blob = new Blob([combinedBuffer], { type: 'audio/mp3' });
      console.log("[VKT] Created MP3 blob of size:", blob.size, "bytes");
      
      // Create an object URL from the blob
      const url = URL.createObjectURL(blob);
      console.log("[VKT] Created object URL for MP3:", url);
      
      // Create a new audio element and play the MP3
      const audioElement = new Audio();
      
      // Add event listeners to track audio element state
      audioElement.addEventListener('loadstart', () => console.log("[VKT] MP3 audio loadstart"));
      audioElement.addEventListener('loadedmetadata', () => console.log("[VKT] MP3 audio loadedmetadata, duration:", audioElement.duration));
      audioElement.addEventListener('canplay', () => console.log("[VKT] MP3 audio canplay"));
      audioElement.addEventListener('play', () => console.log("[VKT] MP3 audio play event"));
      audioElement.addEventListener('playing', () => console.log("[VKT] MP3 audio playing"));
      audioElement.addEventListener('pause', () => console.log("[VKT] MP3 audio paused"));
      audioElement.addEventListener('ended', () => console.log("[VKT] MP3 audio ended"));
      audioElement.addEventListener('error', (e) => console.error("[VKT] MP3 audio error:", e));
      
      // Add these to track timeupdate events (to see playback progress)
      let lastReportedTime = -1;
      audioElement.addEventListener('timeupdate', () => {
        // Report every second to avoid log spam
        const currentTime = Math.floor(audioElement.currentTime);
        if (currentTime !== lastReportedTime) {
          console.log(`[VKT] MP3 playback progress: ${currentTime}s / ${audioElement.duration.toFixed(1)}s (${Math.round((currentTime / audioElement.duration) * 100)}%)`);
          lastReportedTime = currentTime;
        }
      });
      
      // Clean up the URL when done
      audioElement.onended = () => {
        console.log("[VKT] MP3 playback ended, revoking URL:", url);
        URL.revokeObjectURL(url);
      };
      
      // Set the source and play
      audioElement.src = url;
      console.log("[VKT] Set MP3 audio source, attempting to play");
      
      // Play the audio and catch any autoplay restrictions
      try {
        await audioElement.play();
        console.log("[VKT] MP3 audio playback started successfully");
      } catch (playError) {
        console.error("[VKT] Error starting MP3 playback (possibly autoplay restriction):", playError);
        // Try to handle autoplay restrictions by adding a user interaction requirement
        document.body.addEventListener('click', async () => {
          try {
            await audioElement.play();
            console.log("[VKT] MP3 audio playback started after user interaction");
          } catch (retryError) {
            console.error("[VKT] Still failed to play after user interaction:", retryError);
          }
        }, { once: true });
      }
      
    } catch (error) {
      console.error("[VKT] Error in MP3 audio processing:", error);
    }
  };

  return {
    sendMicToWebsocket,
    audioBuffer,
    audioHash,
  };
};